// Bridge client: the site's half of the connector contract (contract/BRIDGE-CONTRACT.md).
// Transport is window.postMessage on the page's own origin. The connector's
// content script announces DM_SCREEN_BRIDGE_READY (with a capability map) on
// load and on every DM_SCREEN_BRIDGE_PING; requests carry a requestId (or
// scanId) that the matching *_RESULT echoes back; long jobs stream *_PROGRESS.
import { useEffect, useSyncExternalStore } from "react";
import { answeredNormally, emptyHealth, observe, withConnector, type Health } from "./state/health";
import { traceRequest } from "./state/sessionTrace";

export const APP_SOURCE = "dm-screen-app";
export const BRIDGE_SOURCE = "dm-screen-browser-bridge";

export interface BridgeStatus {
  connected: boolean;
  version: string;
  capabilities: Record<string, boolean>;
  lastSeenAt: number;
}
type BridgeMessage = { source: string; type: string; [k: string]: any };
type Listener = (message: BridgeMessage) => void;

let status: BridgeStatus = { connected: false, version: "", capabilities: {}, lastSeenAt: 0 };
// Liveness: the connector is "connected" only while it is still answering. A PING
// goes out every BRIDGE_PING_MS even when connected; if nothing has come back for
// BRIDGE_STALE_MS (a few missed pings) we mark it gone, so a connector that died,
// crashed or was removed mid-session turns the badge red instead of staying a
// false green while every write silently times out.
const BRIDGE_PING_MS = 5000;
const BRIDGE_STALE_MS = 14000;
const statusSubscribers = new Set<() => void>();
const listeners = new Set<Listener>();
let installed = false;

/* THE HEALTH BOARD, fed by the traffic that already happens.
 *
 * Every request below reports its outcome here, so "is anything broken right
 * now?" is answered without a single extra message. No probe, no timer: a lane
 * that failed is the truest thing anyone can say about the system it was aimed
 * at, and a synthetic ping would only report on the ping.
 *
 * Deliberately never throws into a request. A fault in the health board must not
 * be able to break the thing it is reporting on. */
let health: Health = emptyHealth();
const healthSubscribers = new Set<() => void>();
function note(lane: string, ok: boolean, error?: string) {
  try {
    health = observe(health, { lane, ok, error, at: Date.now() });
    healthSubscribers.forEach((fn) => fn());
  } catch { /* the request is what matters */ }
}
export function useHealth(): Health {
  install();
  return useSyncExternalStore((fn) => { healthSubscribers.add(fn); return () => healthSubscribers.delete(fn); }, () => health, () => health);
}

function setStatus(next: Partial<BridgeStatus>) {
  status = { ...status, ...next };
  statusSubscribers.forEach((fn) => fn());
  try {
    health = withConnector(health, status.connected, status.version, status.lastSeenAt);
    healthSubscribers.forEach((fn) => fn());
  } catch { /* never break the handshake over a lamp */ }
}

function install() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("message", (event) => {
    if (event.source !== window || event.origin !== window.location.origin) return;
    const data = event.data as BridgeMessage | undefined;
    if (!data || typeof data !== "object" || data.source !== BRIDGE_SOURCE) return;
    // Any message from the connector is proof of life — stamp it (no notify) so the
    // heartbeat's staleness check and the "last seen" display stay honest.
    status.lastSeenAt = Date.now();
    if (data.type === "DM_SCREEN_BRIDGE_READY") {
      setStatus({ connected: true, version: String(data.version || ""), capabilities: data.capabilities || {} });
    } else if (data.type === "DM_SCREEN_BRIDGE_GONE") {
      setStatus({ connected: false });   // the connector told us it is shutting down
    }
    listeners.forEach((fn) => fn(data));
  });
}

// Which Roll20 page token routes work on: the players' page (the map the
// player ribbon is on) or the GM's active page. Set by the Shell from the
// automation settings; injected into every token/turn request.
let roll20PageMode: "players" | "gm" = "players";
export const setRoll20PageMode = (mode: "players" | "gm") => { roll20PageMode = mode; };
const PAGE_ROUTES = new Set(["DM_SCREEN_ROLL20_TOKENS_REQUEST", "DM_SCREEN_ROLL20_BARS_REQUEST", "DM_SCREEN_ROLL20_TURN_ADD_REQUEST"]);
export function post(message: Record<string, unknown>) {
  install();
  if (PAGE_ROUTES.has(String(message.type)) && message.page === undefined) message = { ...message, page: roll20PageMode };
  // The envelope's source is the app id and must win: the connector drops
  // anything else. Payload fields named source travel as `from` (see mutate).
  if ("source" in message && message.source !== APP_SOURCE) throw new Error(`Bridge payload must not carry a source field (${String(message.type)})`);
  window.postMessage({ ...message, source: APP_SOURCE }, window.location.origin);
}

export function ping() { post({ type: "DM_SCREEN_BRIDGE_PING" }); }

export function onBridge(listener: Listener) {
  install();
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const newRequestId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** The connector's current capability map, for NON-React callers that must decide
 *  whether a lane is worth sending at all — an absent capability would only time
 *  out. React components read this reactively through useBridgeStatus(). */
export const bridgeCapabilities = (): Record<string, boolean> => status.capabilities;

/** Send a request and resolve with the first result whose correlation id matches. */
export function request<T = BridgeMessage>(message: Record<string, unknown>, options: { resultType: string; idKey?: string; timeoutMs?: number; onProgress?: (m: BridgeMessage) => void; progressType?: string }): Promise<T> {
  const idKey = options.idKey || "requestId";
  const id = String(message[idKey]);
  const lane = String(message.type);
  const startedAt = Date.now();
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      off();
      const why = `No answer within ${Math.round((options.timeoutMs || 60000) / 1000)} s`;
      note(lane, false, why);
      traceRequest(lane, message, { ok: false, error: why, startedAt });
      reject(new Error(`The connector did not answer ${lane} within ${Math.round((options.timeoutMs || 60000) / 1000)} s`));
    }, options.timeoutMs || 60000);
    const off = onBridge((m) => {
      if (m[idKey] !== undefined && m[idKey] !== id) return;
      if (options.progressType && m.type === options.progressType) { options.onProgress?.(m); return; }
      if (m.type !== options.resultType) return;
      window.clearTimeout(timer); off();
      /* AN ANSWER IS NOT A SUCCESS. Nearly every lane answers {ok, error}, and a
         refused write is exactly the failure the board exists to show. A lane
         that reports no ok field at all is taken at its word: it answered. */
      const ok = answeredNormally(m as any);
      if (!ok) note(lane, false, String(m.error || "Refused, with no reason given"));
      else note(lane, true);
      traceRequest(lane, message, { ok, uncertain: (m as any).uncertain === true, error: m.error ? String((m as any).error) : undefined, startedAt, verified: (m as any).verified === true });
      resolve(m as unknown as T);
    });
    post(message);
  });
}

export function useBridgeStatus(): BridgeStatus {
  install();
  useEffect(() => {
    ping();
    const timer = window.setInterval(() => {
      ping();   // always — a live connector keeps answering; a dead one goes quiet
      if (status.connected && Date.now() - status.lastSeenAt > BRIDGE_STALE_MS) setStatus({ connected: false });
    }, BRIDGE_PING_MS);
    return () => window.clearInterval(timer);
  }, []);
  return useSyncExternalStore((fn) => { statusSubscribers.add(fn); return () => statusSubscribers.delete(fn); }, () => status, () => status);
}
